import React, { useState, useEffect, useMemo } from 'react';
import { FeedbackOption, NegativeFeedback } from '../../types';
import { useReviewParams, useAppointmentDetails, useStepNavigation } from '../organisms/ReviewWrapper';
import reviewService from '../../services/reviewService';
import apruvRateService from '../../services/apruvRateService';
import PoweredByApruv from '../common/PoweredByApruv';
import badReview from '../../assets/images/bad-review.svg';
import faceSad from '../../assets/images/face-sad.svg';
import faceSadActive from '../../assets/images/face-sad-active.svg';
import faceNeutral from '../../assets/images/face-neutral.svg';
import faceNeutralActive from '../../assets/images/face-neutral-active.svg';
import faceSmile from '../../assets/images/face-smile.svg';
import faceSmileActive from '../../assets/images/face-smile-active.svg';
import faceHappy from '../../assets/images/face-happy.svg';
import faceHappyActive from '../../assets/images/face-happy-active.svg';
import './NegativeReview.css';

interface NegativeReviewProps {
  isOldReview?: boolean;
  onStepChange?: (step: string) => void;
}

const SOFT_FEEDBACK_COMPANY_ID = 20;

const NegativeReview: React.FC<NegativeReviewProps> = ({ isOldReview = false, onStepChange }) => {
  const { appointmentId } = useReviewParams();
  const appointmentDetails = useAppointmentDetails(); // Get from context
  const { goToDone } = useStepNavigation();
  
  const [customFeedback, setCustomFeedback] = useState<string>('');
  const [selectedNegativeFeedback, setSelectedNegativeFeedback] = useState<NegativeFeedback[]>([]);
  const [loading, setLoading] = useState(false);

  const possibleFeedbacks: FeedbackOption[] = useMemo(() => [
    { name: 'Bad', value: 1, icon: faceSad, iconActive: faceSadActive },
    { name: 'Okay', value: 2, icon: faceNeutral, iconActive: faceNeutralActive },
    { name: 'Good', value: 3, icon: faceSmile, iconActive: faceSmileActive },
    { name: 'Great', value: 4, icon: faceHappy, iconActive: faceHappyActive },
  ], []);

  const feedbacks: NegativeFeedback[] = [
    { label: 'Overall Service', selected: false },
    { label: 'Service Quality', selected: false },
    { label: 'Efficiency', selected: false },
    { label: 'Transparency', selected: false },
    { label: 'Technician Competence', selected: false },
    { label: 'Customer Experience', selected: false },
    { label: 'Value for Money', selected: false },
    { label: 'Timeliness', selected: false },
  ];

  const [feedbackItems, setFeedbackItems] = useState<NegativeFeedback[]>(feedbacks);
  
  // Get the selected feedback rating from review service or local storage
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackOption | null>(null);

  useEffect(() => {
    // Get the selected rating that brought user to negative review
    const fedId = reviewService.getFedId() || 1;
    const selected = possibleFeedbacks.find(f => f.value === fedId);
    setSelectedFeedback(selected || possibleFeedbacks[0]);
  }, [possibleFeedbacks]);

  // Check if this is NPS mode and if the rating is a passive (7-8)
  const rateConfig = apruvRateService.getRateConfig();
  const isNPS = rateConfig?.ScoreMethod === 'NPS';
  const rating = reviewService.getFedId() || 0;
  const isPassive = isNPS && rating >= 7 && rating <= 8;
  const usesSoftFeedbackLanguage =
    isPassive || appointmentDetails?.appointment_details.CompanyID === SOFT_FEEDBACK_COMPANY_ID;
  const feedbackTitle = usesSoftFeedbackLanguage
    ? 'Thanks for your feedback!'
    : "We're sorry to hear that";
  const feedbackPrompt = usesSoftFeedbackLanguage
    ? 'What can we do better?'
    : 'Tell us what can be improved?';

  const handleSelectImprovement = (item: NegativeFeedback) => {
    const updatedItems = feedbackItems.map(feedback =>
      feedback.label === item.label 
        ? { ...feedback, selected: !feedback.selected }
        : feedback
    );
    setFeedbackItems(updatedItems);
    
    // Update selected negative feedback array
    const selected = updatedItems.filter(f => f.selected);
    setSelectedNegativeFeedback(selected);
  };

  const handleSubmitNegative = async () => {
    if (!selectedFeedback || !appointmentId) return;

    try {
      setLoading(true);
      
      if (!appointmentDetails) return;

      await reviewService.submitReview({
        ID: appointmentDetails.appointment_details.AppointmentId,
        Rating: selectedFeedback.value,
        Feedback: customFeedback,
        Tags: selectedNegativeFeedback.map(f => f.label)
      });

      handleSkipNegative();
    } catch (error) {
      console.error('Error submitting negative review:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipNegative = () => {
    if (isOldReview && onStepChange) {
      onStepChange('done');
    } else {
      goToDone();
    }
  };

  return (
    <div className="negative-review-container">
      <div className="negative-review-content">
        {/* Header Icon */}
        <div className="negative-review-icon">
          <img src={badReview} alt="Feedback" />
        </div>

        {/* Title - Different messaging for passives vs detractors */}
        <div className="negative-review-title">
          <h2>{feedbackTitle}</h2>
        </div>

        {/* Feedback Selection */}
        <div className="negative-review-feedback-section">
          <label className="negative-review-label">
            {feedbackPrompt}
          </label>
          
          <div className="negative-review-chips">
            {feedbackItems.map((feedback) => (
              <button
                key={feedback.label}
                className={`negative-review-chip ${feedback.selected ? 'active' : ''}`}
                onClick={() => handleSelectImprovement(feedback)}
              >
                {feedback.label}
              </button>
            ))}
          </div>

          {/* Custom Feedback Textarea */}
          <div className="negative-review-textarea-container">
            <textarea
              placeholder="Say something here..."
              rows={5}
              className="negative-review-textarea"
              value={customFeedback}
              onChange={(e) => setCustomFeedback(e.target.value)}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="negative-review-actions">
          <button
            onClick={handleSubmitNegative}
            disabled={loading}
            className="negative-review-button primary"
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
          <button
            onClick={handleSkipNegative}
            className="negative-review-button outlined"
          >
            Skip
          </button>
        </div>

        <PoweredByApruv />
      </div>
    </div>
  );
};

export default NegativeReview;
